// src/components/gridSettings/SortableRow.tsx
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CircleMinus, GripVertical, Info } from "lucide-react";
import * as React from "react";
import { Control, useWatch } from "react-hook-form";
import { cn } from "@/utils";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip";
import type { GridSettingsValues } from "./schema";
import type { Column } from "./types";

interface SortableRowProps {
  value: string;
  control: Control<GridSettingsValues>;
  name: `columns.${number}.id`;
  isDragging: boolean;
  availableColumns: Column[];
  onRemove?: () => void;
}

export default function SortableRow({
  availableColumns,
  control,
  name,
  value,
  isDragging,
  onRemove
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: value,
    disabled: name === "columns.0.id"
  });

  const style = React.useMemo<React.CSSProperties>(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition
    }),
    [transform, transition]
  );

  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  const currentFormColumns = useWatch({
    control,
    name: "columns"
  }) as GridSettingsValues["columns"] | undefined;

  return (
    <div ref={setNodeRef} style={style} className="flex items-center rounded-md bg-white">
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const options = availableColumns
            .filter((col) => col.id === field.value || !currentFormColumns?.some((c) => c.id === col.id))
            .map((col) => ({ id: col.id, label: col?.label ? col.label : capitalize(col.id) }));

          return (
            <FormItem className="flex-1">
              <div className="flex items-center gap-2">
                <GripVertical
                  {...attributes}
                  {...listeners}
                  className={cn(
                    "h-5 w-5 text-[#B9B9B9] focus:outline-none cursor-grab",
                    name === "columns.0.id" && "opacity-0 pointer-events-none"
                  )}
                />

                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={name === "columns.0.id"}
                >
                  <FormControl>
                    <SelectTrigger
                      className={cn(
                        "w-full border-[#DDDDDD] data-[disabled]:opacity-100 aria-invalid:border-[#BB0B0E]",
                        name === "columns.0.id" ?
                          "text-[#8B8B8B] bg-[#EAEAEA] cursor-not-allowed"
                        : "cursor-pointer"
                      )}
                    >
                      <SelectValue placeholder="Choose column..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="cursor-pointer overflow-hidden rounded-md [&_[data-radix-select-viewport]]:m-0">
                    {options
                      .sort((a, b) =>
                        a.id === field.value ? -1
                        : b.id === field.value ? 1
                        : 0
                      )
                      .map((opt) => (
                        <SelectItem
                          key={opt.id}
                          value={opt.id}
                          className={cn(
                            "cursor-pointer focus:bg-[#e8edea] first:rounded-t-md last:rounded-b-md hover:text-inherit",
                            opt.id === field.value ? "font-bold bg-[#dae5de] focus:bg-[#dae5de]" : ""
                          )}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {name === "columns.0.id" ?
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-5 w-5" stroke="white" fill="#8B8B8B" />
                      </TooltipTrigger>
                      <TooltipContent
                        align="start"
                        alignOffset={10}
                        className="bg-[#8B8B8B] rounded-none"
                      >
                        <p>Column cannot be removed.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                : <CircleMinus
                    onClick={onRemove}
                    className="h-5 w-5 cursor-pointer"
                    stroke="white"
                    fill="#C32A2C"
                  />
                }
              </div>
              {!isDragging && <FormMessage className={cn("pl-6 text-[#BB0B0E]")} />}
            </FormItem>
          );
        }}
      />
    </div>
  );
}
